import { useId, useMemo } from "react";

import type { UseRovrDuotoneFilterOptions } from "./types";

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return [0, 0, 0];
  }
  return [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
  ];
}

export function useRovrDuotoneFilter({
  dark,
  light,
  gamma = 1,
}: UseRovrDuotoneFilterOptions): {
  defs: JSX.Element;
  style: { filter: string };
  filterId: string;
} {
  const baseId = useId();
  const filterId = useMemo(
    () => `rovr-duotone-${baseId.replace(/:/g, "")}`,
    [baseId]
  );

  const [dr, dg, db] = hexToRgb(dark);
  const [lr, lg, lb] = hexToRgb(light);

  const defs = useMemo(
    () => (
      <svg
        aria-hidden="true"
        style={{ position: "absolute", width: 0, height: 0 }}
      >
        <defs>
          <filter id={filterId} colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="0.299 0.587 0.114 0 0
                      0.299 0.587 0.114 0 0
                      0.299 0.587 0.114 0 0
                      0 0 0 1 0"
            />
            {gamma !== 1 && (
              <feComponentTransfer>
                <feFuncR type="gamma" amplitude="1" exponent={gamma} />
                <feFuncG type="gamma" amplitude="1" exponent={gamma} />
                <feFuncB type="gamma" amplitude="1" exponent={gamma} />
              </feComponentTransfer>
            )}
            <feComponentTransfer>
              <feFuncR type="table" tableValues={`${dr} ${lr}`} />
              <feFuncG type="table" tableValues={`${dg} ${lg}`} />
              <feFuncB type="table" tableValues={`${db} ${lb}`} />
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>
    ),
    [filterId, dr, dg, db, lr, lg, lb, gamma]
  );

  const style = useMemo(() => ({ filter: `url(#${filterId})` }), [filterId]);

  return { defs, style, filterId };
}
